import { Authentication } from 'src/types/dto';

export const LANDING_AUTH_TRANSFER_COOKIE_KEY = 'uticketAuthTransfer';

const AUTH_TRANSFER_MAX_AGE_MS = 2 * 60 * 1000;

type LandingAuthTransferPayload = {
	version?: number;
	issuedAt?: number;
	auth?: Partial<Authentication>;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const safeDecodeCookieValue = (value: string) => {
	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
};

const getCookie = (name: string) => {
	const prefix = `${name}=`;
	const cookie = document.cookie
		.split(';')
		.map(part => part.trim())
		.find(part => part.startsWith(prefix));

	return cookie ? safeDecodeCookieValue(cookie.slice(prefix.length)) : null;
};

export const hasLandingAuthTransferCookie = () => {
	if (typeof window === 'undefined' || typeof document === 'undefined') {
		return false;
	}

	return Boolean(getCookie(LANDING_AUTH_TRANSFER_COOKIE_KEY));
};

const getSharedCookieDomain = (hostname = window.location.hostname) => {
	if (
		hostname === 'localhost' ||
		hostname === '127.0.0.1' ||
		hostname === '::1'
	) {
		return null;
	}

	if (hostname === 'uticket.com.br' || hostname.endsWith('.uticket.com.br')) {
		return '.uticket.com.br';
	}

	if (hostname === 'uticket.dev.br' || hostname.endsWith('.uticket.dev.br')) {
		return '.uticket.dev.br';
	}

	return null;
};

const deleteCookie = (name: string, domain?: string | null) => {
	const domainSegment = domain ? `; domain=${domain}` : '';
	document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainSegment}`;
};

export const clearLandingAuthTransferCookie = () => {
	deleteCookie(LANDING_AUTH_TRANSFER_COOKIE_KEY);
	deleteCookie(LANDING_AUTH_TRANSFER_COOKIE_KEY, '.pr.uticket.dev.br');

	const sharedDomain = getSharedCookieDomain();
	if (sharedDomain) {
		deleteCookie(LANDING_AUTH_TRANSFER_COOKIE_KEY, sharedDomain);
	}
};

const parseTransferPayload = (
	rawValue: string
): LandingAuthTransferPayload | null => {
	try {
		const parsed = JSON.parse(rawValue) as unknown;
		return isRecord(parsed) ? parsed : null;
	} catch {
		return null;
	}
};

const normalizeAuth = (payload: LandingAuthTransferPayload) => {
	const { auth } = payload;

	if (!auth?.email || !auth.token) {
		return null;
	}

	const id = Number(auth.id ?? 0);

	return {
		id: Number.isFinite(id) ? id : 0,
		email: auth.email,
		name: auth.name ?? '',
		token: auth.token,
		userStatus: auth.userStatus ?? '',
		signupSource: auth.signupSource ?? 'landing_page',
	};
};

export const consumeLandingAuthTransfer = (): Authentication | null => {
	if (typeof window === 'undefined' || typeof document === 'undefined') {
		return null;
	}

	const rawValue = getCookie(LANDING_AUTH_TRANSFER_COOKIE_KEY);
	if (!rawValue) {
		return null;
	}

	clearLandingAuthTransferCookie();

	const payload = parseTransferPayload(rawValue);
	if (!payload?.issuedAt || Date.now() - payload.issuedAt > AUTH_TRANSFER_MAX_AGE_MS) {
		return null;
	}

	return normalizeAuth(payload);
};
